import { forwardRef, Ref, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import BaseButton from '@/components/ui/BaseButton';
import { useAppContext } from '@/provider/contexts';
import { handleGetProductInformation, handleSetProductStockStatus } from '@/api/storeManagement/stockControl';
import {
    formatDisplayByKey,
    InfoItem,
    ProductStockData,
    STOCK_STATUS,
    stockLabelInfo,
} from '@/types/pages/storeManagement/stockControl';
import { MobileFeatureHandle, MobileFeatureViewProps } from '@/mobile/types';
import { createOnScanHandler } from '@/mobile/utils/createOnScanHandler';
import { useMobileState } from '@/mobile/provider/context';
import { normalizeIdentifier } from '@/utils/associateUtils';
import { useTranslation } from 'react-i18next';

const EMPTY_STOCK_DATA: ProductStockData = {
    product_name: '',
    product_number: '',
    product_barcode: '',
    associated_devices: [],
    total_associated_devices: 0,
    product_inventory_state: null,
    status: '',
    keyword_type: '',
};

const OutOfStockView = forwardRef(({ dependency }: MobileFeatureViewProps, ref: Ref<MobileFeatureHandle>) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state } = useMobileState();
    const {
        settings,
        refs: { isSendingRef },
        method: { focusCommonEslInput, setESL, setButtonInfo, addHistoryItem },
    } = dependency;
    const [stockEnabled, setStockEnabled] = useState(false);
    const [productStockInfo, setProductStockInfo] = useState<ProductStockData>(EMPTY_STOCK_DATA);
    const [sendingStockAction, setSendingStockAction] = useState(false);

    const resetStock = useCallback(() => {
        setStockEnabled(false);
        setProductStockInfo(EMPTY_STOCK_DATA);
    }, []);

    const fetchStockInfoOfProduct = useCallback(
        async (identifier: string) => {
            isSendingRef.current = true;
            const { normalized } = normalizeIdentifier(identifier, settings);
            setESL(normalized);
            setButtonInfo({ type: 'loading' });
            try {
                const {
                    data: { data, isSuccess, message },
                } = await handleGetProductInformation({
                    store_id: manageStoreId as number,
                    scope: 'esl',
                    keyword: normalized,
                });

                if (isSuccess) {
                    const { payload, http_status, message: itemMessage } = data.results?.[0] ?? {};
                    if (http_status === 200) {
                        setProductStockInfo(payload);
                        setStockEnabled(true);
                        return;
                    }
                    throw new Error(itemMessage || 'Stock information unavailable');
                }
                throw new Error(message?.trim() || 'Failed to query stock info');
            } catch (error) {
                const errorReason = (error as Error).message || 'Unknown error occurred';
                setButtonInfo({ type: 'error', msg: errorReason });
                addHistoryItem({
                    eslMac: identifier,
                    status: 'failed',
                    customMessage: `Stock update failed: ${errorReason}`,
                    errorReason,
                    timestamp: new Date().toLocaleString(),
                });
            } finally {
                isSendingRef.current = false;
            }
        },
        [manageStoreId],
    );

    const handleStockAssociation = useCallback(async () => {
        if (!manageStoreId || !state.esl || sendingStockAction) return;

        setSendingStockAction(true);
        setButtonInfo({ type: 'sending' });

        try {
            const nextInventoryState =
                productStockInfo.product_inventory_state === STOCK_STATUS.IN_STOCK
                    ? STOCK_STATUS.OUT_OF_STOCK
                    : STOCK_STATUS.IN_STOCK;

            const {
                data: { isSuccess, message, status },
            } = await handleSetProductStockStatus({
                keyword: state.esl,
                store_id: manageStoreId,
                inventory_state: nextInventoryState,
                do_push_devices: true,
            });

            if (!isSuccess) {
                if (status === 400 || status === 401) {
                    throw new Error(message?.trim() || 'Failed to update stock status');
                }
                throw new Error('Failed to update stock status');
            }

            setButtonInfo({ type: 'success' });
            addHistoryItem({
                eslMac: state.esl,
                status: 'success',
                customMessage:
                    nextInventoryState === STOCK_STATUS.OUT_OF_STOCK
                        ? 'Stock status set to Out of Stock'
                        : 'Stock status restored to Normal Display',
                timestamp: new Date().toLocaleString(),
            });
        } catch (error) {
            const errorReason = (error as Error).message || 'Unknown error occurred';
            setNotification({ message: errorReason, severity: 'error' });
            setButtonInfo({ type: 'error', msg: errorReason });
            addHistoryItem({
                eslMac: state.esl,
                status: 'failed',
                customMessage: `Stock update failed: ${errorReason}`,
                errorReason,
                timestamp: new Date().toLocaleString(),
            });
        } finally {
            setSendingStockAction(false);
            setESL('');
            resetStock();
        }
    }, [manageStoreId, productStockInfo.product_inventory_state, sendingStockAction, state.esl]);

    const onEslEnter = useCallback((value: string) => fetchStockInfoOfProduct(value), [fetchStockInfoOfProduct]);

    const onScan = useMemo(
        () =>
            createOnScanHandler({
                shouldProcess: () => !state.isSubmitting,
                onNormalizedScan: onEslEnter,
            }),
        [state.isSubmitting, onEslEnter],
    );

    const onModeChange = useCallback(() => {
        focusCommonEslInput();
    }, []);

    // Implement methods that parent use.
    useImperativeHandle(
        ref,
        () => ({
            onEslEnter,
            onScan,
            onModeChange,
            onClear: resetStock,
        }),
        [onEslEnter, onScan, onModeChange, resetStock],
    );

    return (
        <>
            {stockEnabled && (
                <Box
                    data-testid="mobile-stock-panel"
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                        mb: 1,
                    }}
                >
                    <Box display="table">
                        {stockLabelInfo.map((item: InfoItem, index: number) => {
                            if (item.isArray) {
                                return (productStockInfo[item.key] as string[]).map((value, arrIndex) => (
                                    <Box key={`${item.key}-${arrIndex}`} display="table-row">
                                        <Typography display="table-cell" sx={{ pr: 1, whiteSpace: 'nowrap' }}>
                                            {arrIndex === 0 ? item.label : ''}
                                        </Typography>
                                        <Typography display="table-cell" sx={{ px: 1 }}>
                                            {arrIndex === 0 ? ':' : ''}
                                        </Typography>
                                        <Typography display="table-cell">
                                            {formatDisplayByKey(item.key, value)}
                                        </Typography>
                                    </Box>
                                ));
                            }

                            return (
                                <Box key={`${item.key}-${index}`} display="table-row">
                                    <Typography display="table-cell" sx={{ pr: 1, whiteSpace: 'nowrap' }}>
                                        {item.label}
                                    </Typography>
                                    <Typography display="table-cell" sx={{ px: 1 }}>
                                        :
                                    </Typography>
                                    <Typography display="table-cell">
                                        {formatDisplayByKey(item.key, String(productStockInfo[item.key] ?? ''))}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <BaseButton
                            data-testid="mobile-stock-action-btn"
                            variant="contained"
                            color="primary"
                            loading={sendingStockAction}
                            disabled={sendingStockAction}
                            onClick={() => {
                                void handleStockAssociation();
                            }}
                        >
                            {productStockInfo.product_inventory_state === STOCK_STATUS.OUT_OF_STOCK
                                ? t('Restore to Normal')
                                : t('Out of Stock')}
                        </BaseButton>
                        <BaseButton
                            data-testid="mobile-stock-cancel-btn"
                            variant="outlined"
                            color="secondary"
                            disabled={sendingStockAction}
                            onClick={() => resetStock()}
                        >
                            {t('Cancel')}
                        </BaseButton>
                    </Stack>
                </Box>
            )}
        </>
    );
});

export default OutOfStockView;
